// @flow
import { useCallback, useEffect, useState } from 'react';
import useFormManager from '../../../common/hooks/useFormManager';
import useValidatedField from '../../../common/hooks/useValidatedField';
import useUpdateEffect from '../../../common/hooks/useUpdateEffect';
import useAccountCreation from './useAccountCreation';
import useLoadingDialog from './useLoadingDialog';
import useCaptcha from './useCaptcha';

const DEFAULT_COUNTRY = 'US';
const DEFAULT_DIAL_CODE = '1';

export default function useNewAccountFormManager(isCoach: boolean) {
    const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY);
    const [dialCode, setDialCode] = useState(DEFAULT_DIAL_CODE);
    const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
    const [hasAgreedToSMS, setHasAgreedToSMS] = useState(false);

    const { showValidationErrors, setShowValidationErrors, notNullValidator, emailValidator } = useFormManager();

    const { HCAPTCHA_SITE_KEY, captchaRef, captchaRemountKey, getCaptchaToken, resetCaptcha } = useCaptcha();

    const {
        value: name,
        setValue: setName,
        validated: nameValidated,
        setValidated: setNameValidated,
        errorState: nameErrorMessage
    } = useValidatedField(notNullValidator);

    const {
        value: email,
        setValue: setEmail,
        validated: emailValidated,
        setValidated: setEmailValidated,
        errorState: emailErrorMessage
    } = useValidatedField(emailValidator);

    const {
        value: password,
        setValue: setPassword,
        validated: passwordValidated,
        setValidated: setPasswordValidated,
        errorState: passwordErrorMessage
    } = useValidatedField(notNullValidator);

    const [organization, setOrganization] = useState('');

    const optionalFieldValidator = useCallback(() => null, []);
    const {
        value: expectedAccountSize,
        setValue: setExpectedAccountSize,
        validated: expectedAccountSizeValidated,
        setValidated: setExpectedAccountSizeValidated,
        errorState: expectedAccountSizeErrorMessage
    } = useValidatedField(optionalFieldValidator);

    const phoneValidator = useCallback(
        (text: string) => {
            const digitsOnly = text.replace(/\D/g, '');
            const nationalNumberLength = digitsOnly.length - dialCode.length;
            if (nationalNumberLength <= 0) return null;
            if (nationalNumberLength < 7 || nationalNumberLength > 12) return 'Invalid phone number';
            return null;
        },
        [dialCode]
    );

    const {
        value: phoneNumber,
        setValue: setPhoneNumber,
        validated: phoneValidated,
        setValidated: setPhoneValidated,
        errorState: phoneErrorMessage
    } = useValidatedField(phoneValidator);

    const { isOpen, progress, startProgress, stopProgress } = useLoadingDialog();

    const {
        createNewCoachAccount,
        createNewAthleteAccount,
        creatingInProcess,
        creatingComplete,
        setCreatingComplete,
        setCreatingInProcess,
        serverErrorMessages,
        serverError,
        setServerError
    } = useAccountCreation(resetCaptcha);

    const createAccountSuccessfulOrNotSubmitted = creatingComplete === null ? true : creatingComplete;

    const onCreateAccountStateChange = () => {
        if (Object.keys(serverErrorMessages).length > 0) {
            setNameValidated(serverErrorMessages.full_name === undefined && serverErrorMessages.fullName === undefined);
            setEmailValidated(serverErrorMessages.email === undefined);
            setPasswordValidated(serverErrorMessages.password === undefined);
            setExpectedAccountSizeValidated(serverErrorMessages.expectedAccountSize === undefined);
            setPhoneValidated(serverErrorMessages.phoneNumber === undefined);
        } else {
            setNameValidated(createAccountSuccessfulOrNotSubmitted);
            setEmailValidated(createAccountSuccessfulOrNotSubmitted);
            setPasswordValidated(createAccountSuccessfulOrNotSubmitted);
            setExpectedAccountSizeValidated(createAccountSuccessfulOrNotSubmitted);
            setPhoneValidated(createAccountSuccessfulOrNotSubmitted);
        }
    };

    useUpdateEffect(onCreateAccountStateChange, [
        createAccountSuccessfulOrNotSubmitted,
        setNameValidated,
        setEmailValidated,
        setPasswordValidated,
        serverErrorMessages
    ]);

    useEffect(() => {
        setCreatingInProcess(false);
        setCreatingComplete(null);
    }, [name, email, password, organization, phoneNumber, expectedAccountSize, setCreatingComplete, setCreatingInProcess]);

    const isInProcess = creatingComplete || creatingInProcess;

    const createAthleteAccount = async () => {
        const formFieldsValid = nameValidated && emailValidated && passwordValidated && hasAgreedToTerms;

        const canCreateAccount = formFieldsValid && !isInProcess;

        setShowValidationErrors(true);

        if (!canCreateAccount) {
            return;
        }

        setCreatingInProcess(true);

        const captchaToken = await getCaptchaToken();
        if (!captchaToken) {
            setServerError(true);
            resetCaptcha();
            setCreatingInProcess(false);
            return;
        }

        startProgress();

        let response;
        try {
            response = await createNewAthleteAccount(name, email, password, captchaToken);
        } catch (e) {
            stopProgress();
            setCreatingInProcess(false);
            throw e;
        }
        if (!response || !response.session_id) {
            stopProgress();
        }
    };

    const createAccount = async () => {
        if (!isCoach) {
            return createAthleteAccount();
        }

        const formFieldsValid =
            nameValidated && emailValidated && passwordValidated && phoneValidated && hasAgreedToTerms;

        const canCreateAccount = formFieldsValid && !isInProcess;

        setShowValidationErrors(true);

        if (!canCreateAccount) {
            return;
        }

        setCreatingInProcess(true);

        const captchaToken = await getCaptchaToken();
        if (!captchaToken) {
            setServerError(true);
            resetCaptcha();
            setCreatingInProcess(false);
            return;
        }

        startProgress();

        /* eslint-disable-next-line */
        const formattedNumber = phoneNumber.replace(/[\(\)\-\+ ]+/g, '');

        const expectedAccountSizeAsInt = parseInt(expectedAccountSize, 10);

        const coachUser = {
            fullName: name,
            email,
            password,
            phoneNumber: formattedNumber || '',
            country: countryCode,
            organization: organization || null,
            expectedAccountSize: Number.isNaN(expectedAccountSizeAsInt) ? null : expectedAccountSizeAsInt,
            accountCreationLocation: 'account',
            token: captchaToken,
            hasAgreedToSMS
        };

        let response;
        try {
            response = await createNewCoachAccount(coachUser);
        } catch (e) {
            stopProgress();
            setCreatingInProcess(false);
            throw e;
        }
        if (!response || !response.session_id) {
            stopProgress();
        }
    };

    return {
        name,
        setName,
        nameValidated,
        nameErrorMessage,
        email,
        setEmail,
        emailValidated,
        emailErrorMessage,
        password,
        setPassword,
        passwordValidated,
        passwordErrorMessage,
        hasAgreedToTerms,
        setHasAgreedToTerms,
        hasAgreedToSMS,
        setHasAgreedToSMS,
        expectedAccountSize,
        setExpectedAccountSize,
        expectedAccountSizeValidated,
        setExpectedAccountSizeValidated,
        expectedAccountSizeErrorMessage,
        showValidationErrors,
        isInProcess,
        isOpen,
        progress,
        createAccount,
        createAccountSuccessfulOrNotSubmitted,
        serverErrorMessages,
        organization,
        setOrganization,
        setCountryCode,
        dialCode,
        setDialCode,
        phoneNumber,
        setPhoneNumber,
        phoneValidated,
        phoneErrorMessage,
        serverError,
        setServerError,
        HCAPTCHA_SITE_KEY,
        captchaRef,
        captchaRemountKey
    };
}
